import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Lock, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ResetPasswordPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    if (password !== confirm) {
      setError("Passwords don't match");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/auth/reset-password`, {
        token,
        new_password: password,
      });
      setDone(true);
      setTimeout(() => navigate("/login", { replace: true }), 2200);
    } catch (err) {
      setError(err?.response?.data?.detail || "Reset failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-950 px-6">
      <div className="w-full max-w-sm">
        <div className="flex justify-center mb-8">
          <img
            src="https://customer-assets.emergentagent.com/job_shoot-organizer-1/artifacts/3jmotfdo_WC%20Logo%202018%20FULL%20Outlined%20WHITE.png"
            alt="Whiplash Film & Creative"
            className="h-8"
          />
        </div>
        <div className="border border-zinc-800 bg-zinc-900/60 rounded-md p-7">
          {done ? (
            <div className="text-center py-2">
              <div className="w-12 h-12 mx-auto rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center mb-4">
                <CheckCircle2 className="w-6 h-6 text-emerald-400" />
              </div>
              <h1 className="font-heading text-xl text-zinc-100 mb-2">Password updated</h1>
              <p className="text-sm text-zinc-500">Redirecting you to sign in…</p>
            </div>
          ) : (
            <>
              <div className="text-overline text-zinc-500 mb-1">Set a new password</div>
              <h1 className="font-heading text-2xl text-zinc-100 mb-5 leading-tight">
                Choose a new password
              </h1>
              <form onSubmit={onSubmit} className="space-y-3">
                <div className="relative">
                  <Lock className="w-3.5 h-3.5 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
                  <Input
                    data-testid="reset-new-password"
                    type="password"
                    autoFocus
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="New password (min 8 chars)"
                    className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500 pl-9"
                  />
                </div>
                <div className="relative">
                  <Lock className="w-3.5 h-3.5 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
                  <Input
                    data-testid="reset-confirm-password"
                    type="password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    placeholder="Confirm new password"
                    className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500 pl-9"
                  />
                </div>
                {error && (
                  <div className="text-xs text-rose-400 bg-rose-500/10 border border-rose-500/30 rounded-sm px-3 py-2 flex items-start gap-2">
                    <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                    <span>{error}</span>
                  </div>
                )}
                <Button
                  data-testid="reset-submit-btn"
                  type="submit"
                  disabled={submitting || !password || !confirm}
                  className="w-full bg-amber-500 text-black hover:bg-amber-400 rounded-sm font-semibold"
                >
                  {submitting ? (
                    <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Updating…</>
                  ) : (
                    "Update password"
                  )}
                </Button>
              </form>
            </>
          )}
          {!done && (
            <div className="text-center mt-5">
              <Link to="/login" className="text-xs text-zinc-500 hover:text-amber-500">
                ← Back to sign in
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
